import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

const ConversationDetail = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [flagData, setFlagData] = useState({
    issue_type: 'potential_refusal',
    description: '',
    severity: 'medium'
  });
  const [flagging, setFlagging] = useState(false);
  const [flagSuccess, setFlagSuccess] = useState(false);
  const [expandedIssues, setExpandedIssues] = useState(new Set());

  const fetchConversationDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/conversations/${conversationId}`);
      setConversation(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load conversation');
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    fetchConversationDetails();
  }, [fetchConversationDetails]);

  const handleFlag = async () => {
    if (!flagData.description.trim()) {
      alert('Please enter a description');
      return;
    }

    try {
      setFlagging(true);
      await api.post(`/admin/conversations/${conversationId}/flag`, flagData);
      setFlagSuccess(true);
      setFlagModalOpen(false);
      
      // Reset form
      setFlagData({
        issue_type: 'potential_refusal',
        description: '',
        severity: 'medium'
      });

      // Show success message for 3 seconds
      setTimeout(() => setFlagSuccess(false), 3000);

      // Refresh conversation data
      await fetchConversationDetails();
    } catch (err) {
      alert('Failed to flag conversation: ' + (err.response?.data?.detail || err.message));
    } finally {
      setFlagging(false);
    }
  };

  const exportAsJSON = () => {
    const dataStr = JSON.stringify(conversation, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `conversation-${conversationId}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportAsCSV = () => {
    // Extract sites from all messages
    const sites = new Set();
    const messages = conversation?.messages || [];
    
    messages.forEach(msg => {
      if (msg.role === 'assistant') {
        // Extract domains using regex
        const domains = msg.content.match(/\b(?:www\.)?([a-z0-9-]+\.)+(?:com|org|net|io|co|de|uk|ca|in|edu|gov)\b/gi) || [];
        domains.forEach(domain => {
          sites.add(domain.toLowerCase());
        });
      }
    });

    // Create CSV
    let csv = 'Site\n';
    sites.forEach(site => {
      csv += `${site}\n`;
    });

    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sites-${conversationId}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const getIssueSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getIssueTypeLabel = (type) => {
    const labels = {
      'potential_refusal': '🚫 Potential Refusal',
      'duplicate_sites': '📋 Duplicate Sites',
      'insufficient_results': '⚠️ Insufficient Results',
      'incomplete_analysis': '❌ Incomplete Analysis'
    };
    return labels[type] || type;
  };

  const getIssueTypeColor = (type) => {
    switch (type) {
      case 'potential_refusal':
        return 'bg-red-50 border-l-4 border-red-500';
      case 'duplicate_sites':
        return 'bg-yellow-50 border-l-4 border-yellow-500';
      case 'insufficient_results':
        return 'bg-blue-50 border-l-4 border-blue-500';
      case 'incomplete_analysis':
        return 'bg-orange-50 border-l-4 border-orange-500';
      default:
        return 'bg-gray-50 border-l-4 border-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
        <button
          onClick={() => navigate('/admin/conversation-review')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Back to Conversations
        </button>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center text-gray-500">Conversation not found</div>
        <button
          onClick={() => navigate('/admin/conversation-review')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Back to Conversations
        </button>
      </div>
    );
  }

  const metadata = conversation.metadata || {};
  const messages = conversation.messages || [];
  const issues = conversation.issues_detected || [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => navigate('/admin/conversation-review')}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  ← Back
                </button>
                <h1 className="text-2xl font-bold text-gray-900">
                  {metadata.user_name} - {metadata.vertical || 'General'}
                </h1>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {metadata.user_role?.replace('_', ' ')} • {metadata.message_count || 0} messages • {new Date(metadata.created_at).toLocaleString()}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              {flagSuccess && (
                <div className="bg-green-100 text-green-800 px-3 py-2 rounded-md text-sm">
                  ✅ Flagged successfully
                </div>
              )}
              <button
                onClick={() => setFlagModalOpen(true)}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 font-medium"
              >
                🚩 Flag Issue
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Bar */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white shadow rounded-lg p-4">
            <div className="text-sm font-medium text-gray-500">Total Messages</div>
            <div className="mt-2 text-2xl font-bold text-gray-900">{messages.length}</div>
          </div>
          <div className="bg-white shadow rounded-lg p-4">
            <div className="text-sm font-medium text-gray-500">Issues Detected</div>
            <div className={`mt-2 text-2xl font-bold ${issues.length > 0 ? 'text-orange-600' : 'text-green-600'}`}>
              {issues.length}
            </div>
          </div>
          <div className="bg-white shadow rounded-lg p-4">
            <div className="text-sm font-medium text-gray-500">Conversation Duration</div>
            <div className="mt-2 text-sm text-gray-900">
              {new Date(metadata.created_at).toLocaleDateString()}
            </div>
          </div>
          <div className="bg-white shadow rounded-lg p-4">
            <div className="text-sm font-medium text-gray-500">Actions</div>
            <div className="mt-2 flex space-x-2">
              <button
                onClick={exportAsJSON}
                className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200"
              >
                JSON
              </button>
              <button
                onClick={exportAsCSV}
                className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200"
              >
                CSV
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Messages */}
          <div className="lg:col-span-2">
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Conversation</h2>
              </div>

              <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
                {messages.length === 0 ? (
                  <div className="px-6 py-4 text-center text-gray-500">
                    No messages in this conversation
                  </div>
                ) : (
                  messages.map((message, idx) => (
                    <div key={idx} className={`px-6 py-4 ${message.role === 'assistant' ? 'bg-blue-50' : 'bg-white'}`}>
                      <div className="flex items-start space-x-3">
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-sm ${
                          message.role === 'assistant' ? 'bg-blue-600' : 'bg-gray-600'
                        }`}>
                          {message.role === 'assistant' ? 'AI' : 'U'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 mb-1">
                            {message.role === 'assistant' ? 'Assistant' : 'User'}
                          </div>
                          <div className="text-sm text-gray-700 break-words whitespace-pre-wrap line-clamp-5">
                            {message.content}
                          </div>
                          {message.content && message.content.length > 500 && (
                            <div className="text-xs text-gray-500 mt-2">
                              {Math.ceil(message.content.length / 500)} large message(s)
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Highlight issues for this message */}
                      {issues.filter(i => i.message_index === idx).map((issue, i) => (
                        <div key={i} className={`mt-3 p-3 rounded ${getIssueTypeColor(issue.type)}`}>
                          <div className="text-sm font-semibold text-gray-900">
                            {getIssueTypeLabel(issue.type)}
                          </div>
                          {issue.pattern && (
                            <div className="text-xs text-gray-700 mt-1">
                              Pattern: <code className="bg-gray-200 px-1 py-0.5 rounded">{issue.pattern}</code>
                            </div>
                          )}
                          {issue.total_mentions && (
                            <div className="text-xs text-gray-700 mt-1">
                              Mentioned {issue.total_mentions} times ({issue.unique_sites} unique) — {issue.duplicates} duplicates
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Issues Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow rounded-lg sticky top-4">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  🔍 Detected Issues ({issues.length})
                </h2>
              </div>

              <div className="divide-y divide-gray-200">
                {issues.length === 0 ? (
                  <div className="px-6 py-4 text-center">
                    <div className="text-2xl mb-2">✅</div>
                    <div className="text-sm text-gray-500">No issues detected</div>
                  </div>
                ) : (
                  issues.map((issue, idx) => (
                    <div key={idx} className={`p-4 ${getIssueTypeColor(issue.type)}`}>
                      <div className="cursor-pointer" onClick={() => {
                        const newExpanded = new Set(expandedIssues);
                        if (newExpanded.has(idx)) {
                          newExpanded.delete(idx);
                        } else {
                          newExpanded.add(idx);
                        }
                        setExpandedIssues(newExpanded);
                      }}>
                        <div className="flex items-start justify-between">
                          <div className="font-medium text-sm text-gray-900">
                            {getIssueTypeLabel(issue.type)}
                          </div>
                          <span className="text-xs text-gray-600">
                            {expandedIssues.has(idx) ? '▼' : '▶'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          Message #{issue.message_index + 1}
                        </div>
                      </div>

                      {expandedIssues.has(idx) && (
                        <div className="mt-3 pt-3 border-t text-xs text-gray-700 space-y-2">
                          {issue.pattern && (
                            <div>
                              <strong>Pattern:</strong>
                              <div className="bg-gray-100 px-2 py-1 rounded mt-1 break-all">
                                "{issue.pattern}"
                              </div>
                            </div>
                          )}
                          {issue.snippet && (
                            <div>
                              <strong>Snippet:</strong>
                              <div className="bg-gray-100 px-2 py-1 rounded mt-1 break-all">
                                {issue.snippet}
                              </div>
                            </div>
                          )}
                          {issue.duplicates !== undefined && (
                            <div>
                              <strong>Site Statistics:</strong>
                              <ul className="mt-1 space-y-1">
                                <li>• Total mentions: {issue.total_mentions}</li>
                                <li>• Unique sites: {issue.unique_sites}</li>
                                <li>• Duplicates: {issue.duplicates}</li>
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Flags Section */}
            {metadata.flags && metadata.flags.length > 0 && (
              <div className="bg-white shadow rounded-lg mt-4">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900">
                    🚩 Flags ({metadata.flags.length})
                  </h3>
                </div>
                <div className="divide-y divide-gray-200">
                  {metadata.flags.map((flag, idx) => (
                    <div key={idx} className={`p-4 ${getIssueSeverityColor(flag.severity)}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-medium text-sm">
                            {flag.issue_type}
                          </div>
                          <div className="text-xs mt-1">
                            {flag.description}
                          </div>
                          <div className="text-xs mt-2 opacity-75">
                            by {flag.flagged_by} on {new Date(flag.flagged_at).toLocaleString()}
                          </div>
                        </div>
                        <div className="text-xs font-semibold">
                          {flag.severity}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Flag Modal */}
      {flagModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Flag This Conversation</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Issue Type
                </label>
                <select
                  value={flagData.issue_type}
                  onChange={(e) => setFlagData({ ...flagData, issue_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="potential_refusal">🚫 Potential Refusal</option>
                  <option value="duplicate_sites">📋 Duplicate Sites</option>
                  <option value="insufficient_results">⚠️ Insufficient Results</option>
                  <option value="incomplete_analysis">❌ Incomplete Analysis</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Severity
                </label>
                <select
                  value={flagData.severity}
                  onChange={(e) => setFlagData({ ...flagData, severity: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={flagData.description}
                  onChange={(e) => setFlagData({ ...flagData, description: e.target.value })}
                  rows="4"
                  placeholder="Describe the issue..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>

            <div className="mt-6 flex space-x-3">
              <button
                onClick={() => setFlagModalOpen(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleFlag}
                disabled={flagging}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:bg-gray-400"
              >
                {flagging ? 'Flagging...' : '🚩 Flag'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationDetail;
